import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import toast from 'react-hot-toast';
import App from './App';
import './index.css';

if (process.env.NODE_ENV === 'production') {
  const originalError = toast.error;
  type ToastError = typeof originalError;
  const toastRef = toast as unknown as { error: ToastError };

  toastRef.error = ((message: Parameters<ToastError>[0], options?: Parameters<ToastError>[1]) => {
    if (typeof message === 'string') {
      const normalized = message.trim().toLowerCase();
      const shouldSuppress =
        normalized.includes('failed to load') ||
        normalized.includes('failed to fetch') ||
        normalized.includes('unable to load') ||
        normalized.includes('unable to fetch') ||
        normalized.includes('unable to reach the server right now') ||
        normalized.includes('could not load') ||
        normalized.includes('could not fetch');

      if (shouldSuppress) {
        return '';
      }
    }

    return originalError(message, options);
  }) as ToastError;
}

// Register service worker (production only) — required for web push subscription;
// usePushNotifications() awaits navigator.serviceWorker.ready, which never resolves
// without a registration.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Error boundary for React
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {

    // Log error to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      // Add error logging service here

    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We're sorry, but something unexpected happened. Please refresh the page and try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Refresh Page
            </button>
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400">
                  Error Details
                </summary>
                <pre className="mt-2 text-xs text-red-600 dark:text-red-400 overflow-auto">
                  {this.state.error?.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Initialize app
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>
);
